import type { Market } from "@/lib/api";
import type { PriceRow } from "@/lib/api";

type MarketRouteCandidate = Pick<Market, "slug" | "name" | "cityName"> & Pick<Partial<Market>, "marketType">;

/** Fiyat satırındaki kaynak türü kalıcı kurum URL'sini belirler. */
export function priceMarketHref(row: Pick<PriceRow, "marketSlug" | "marketType">): string {
  if (row.marketType === "borsa") return `/borsa/${row.marketSlug}`;
  if (row.marketType === "resmi" || row.marketType === "kooperatif") return "/borsa";
  return `/hal/${row.marketSlug}`;
}

export function normalizeMarketCity(value: string): string {
  return value.trim().toLocaleLowerCase("tr-TR");
}

/** Bir şehir açıldığında merkez halini, yoksa alfabetik ilk hali seçer. */
export function primaryMarketForCity<T extends MarketRouteCandidate>(markets: T[], city: string): T | undefined {
  const normalizedCity = normalizeMarketCity(city);
  return markets
    .filter((market) => normalizeMarketCity(market.cityName) === normalizedCity)
    .toSorted((left, right) => {
      const leftCenter = /merkez/i.test(left.name) ? 0 : 1;
      const rightCenter = /merkez/i.test(right.name) ? 0 : 1;
      return leftCenter - rightCenter || left.name.localeCompare(right.name, "tr");
    })[0];
}

/** Harita il tiklamasi her zaman bir hal detayina gider; borsa ayni rotaya karismaz. */
export function primaryHalForCity<T extends MarketRouteCandidate>(markets: T[], city: string): T | undefined {
  return primaryMarketForCity(
    markets.filter((market) => (market.marketType ?? "hal") === "hal"),
    city,
  );
}

/** Şehirdeki ticaret borsaları arasından deterministik ana kaynağı seçer. */
export function primaryBorsaForCity<T extends MarketRouteCandidate>(markets: T[], city: string): T | undefined {
  return primaryMarketForCity(
    markets.filter((market) => market.marketType === "borsa"),
    city,
  );
}

/** Tüm kaynak görünümünde hali, hal yoksa ticaret borsasını açar. */
export function primaryPriceSourceForCity<T extends MarketRouteCandidate>(markets: T[], city: string): T | undefined {
  return primaryHalForCity(markets, city) ?? primaryBorsaForCity(markets, city);
}
